import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { get, isEmpty, isArray } from 'lodash'
import { Controller, useForm } from 'react-hook-form'
import * as z from 'zod'

import { useI18n } from '@ggs/i18n'
import { getViewedPageParams } from '@ggs/utils'

import { LocationField } from '../fields'
import { Button } from '../../buttons'
import { SelectInput } from '../../inputs'
import { ResultsList } from '../../results'
import { useDrupalApi, use211Search, useLocation, usePosition } from '../../../local/hooks'
import { syncFormToQueryString } from '../../../local/utils'

import './PremadeSearchWithResultsForm.scss'

/**
 * @typedef {import("../../../definitions").SelectOption} SelectOption
 */

const PremadeSearchWithResultsForm = () => {
  const { t } = useI18n()
  const [filtersByLanguage, setFiltersByLanguage] = useState(new Set([t('English'), t('French')]))
  /**
   * @type {SelectOption[]}
   */
  const defaultOptions = []
  const [subcategoryOptions, setSubcategoryOptions] = useState(defaultOptions)
  const { getSubcategories } = useDrupalApi()
  const defaultNid = '14'
  const categoryNid = get(window || {}, 'drupalSettings.react.currentNodeId', defaultNid)
  const [showResults, setShowResults] = useState(true)
  const formRef = useRef(null)
  const queryStringValues = getViewedPageParams({
    searchDistance: '50',
  })

  /**
   * Resolve subcategories for current category type.
   */
  const resolveSubcategories = async () => {
    const subcategories = await getSubcategories(categoryNid)
    if (subcategories) {
      // Transform node into form select options representation.
      const subcategoryOptions = subcategories.map((subcategory) => {
        /**
         * @type {SelectOption}
         */
        return {
          label: subcategory.title,
          value: isArray(subcategory.code) ? subcategory.code.join('|') : subcategory.code,
        }
      })

      setSubcategoryOptions(subcategoryOptions)
    }
  }

  useEffect(() => {
    if (categoryNid || isEmpty(subcategoryOptions)) {
      resolveSubcategories().then(() => console.log('Resolved subcategories'))
    }
  }, [])

  /**
   * @typedef {object} formSchema
   * @property {string} searchCategory
   * @property {string} address
   * @property {number} page
   */
  const formSchema = z.object({
    searchCategory: z.string().min(1, { message: t('Error: Cannot search without a category') }),
    address: z.string().min(1, { message: t('Error: Cannot search without a location') }),
    searchDistance: z.enum(['10', '20', '50', '100', '200', '400']),
  })

  // Fetch the 211 search data
  const {
    handleSubmit,
    control,
    reset,
    resetField,
    getValues,
    setValue,
    setError,
    clearErrors,
    register,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: queryStringValues,
    resolver: zodResolver(formSchema),
  })

  const [searchAddress, setSearchAddress] = useState(queryStringValues?.address || '')
  const { address, lat, lng } = usePosition()

  console.log('address?', address)

  // If address is grabbed from the browser, set the form value
  useLayoutEffect(() => {
    if (address) {
      console.log('address =>', address)
      setValue('address', address)
      const safeAddress = encodeURI(address)
      setSearchAddress(safeAddress)

      // Address field is not a controlled input, so we need to set the value manually.
      const addressField = formRef.current?.querySelector('input[name="address"]')
      if (addressField) {
        addressField.value = address
      }
    }
  }, [address, formRef])

  // Fetch the location coordinates data
  const {
    data: geoData,
    isFetching: isLocationFetching,
    refetch: fetchLocation,
  } = useLocation(searchAddress, { address, lat, lng })

  const geoLat = geoData?.lat || lat
  const geoLng = geoData?.lng || lng

  /**
   * Disable enter key for location field because the autocomplete selection takes priority
   * @param {KeyboardEvent} e
   */

  const pageSize = 25
  const distance = parseInt(queryStringValues.searchDistance ?? '50')
  const page = parseInt(queryStringValues?.page ?? '0')
  const serviceLanguage = queryStringValues?.serviceLanguage ?? ''
  const searchSort = queryStringValues?.sort ?? 'best'

  // Fetch the 211 search data
  const {
    searchQuery,
    pagination,
    fetchSearch,
    setSearchTerm,
    setSearchSort,
    setSearchDistance,
    setServiceLanguage,
    setRemoveDigital,
    prevSearches,
    searchParams,
  } = use211Search(
    'premade',
    geoLat,
    geoLng,
    true,
    distance,
    pageSize,
    page,
    serviceLanguage,
    searchSort
  )

  const { data: searchData, isFetching: isSearchFetching } = searchQuery
  const { pageIndex, startResult, endResult } = pagination
  const { resetPagination, setPageIndex } = pagination.handlers
  const isDataLoading = isLocationFetching || isSearchFetching
  const numPages = Math.ceil(searchData.RecordCount / pageSize)

  /**
   * Change current page index.
   * @param {number} newPageIndex
   */
  const changePage = (newPageIndex) => {
    setPageIndex(newPageIndex)
    syncFormToQueryString({
      page: newPageIndex,
    })
  }

  /**
   * Change current distance filter
   * @param {number} newDistance
   */
  const changeDistance = (newDistance) => {
    setSearchDistance(newDistance)
    syncFormToQueryString({
      searchDistance: newDistance,
    })
  }

  /**
   * Change current service language filter
   * @param {string} newLanguage
   */
  const changeLanguage = (newLanguage) => {
    setServiceLanguage(newLanguage)
    syncFormToQueryString({
      serviceLanguage: newLanguage,
    })
  }

  /**
   * Change current search sort filter
   * @param {string} newSort
   */
  const changeSort = (newSort) => {
    setSearchSort(newSort)
    syncFormToQueryString({
      sort: newSort,
    })
  }

  /**
   * On submit handler for quick search form
   * @param {formSchema} formValues
   * @return {Promise<void>}
   */
  const onSubmit = async (formValues) => {
    setShowResults(true)
    // if form is being submitted, set default sort, page index, distance, and language
    setSearchSort(queryStringValues.sort ?? 'best')
    setServiceLanguage(queryStringValues.serviceLanguage ?? '')
    if (pageIndex !== 0) resetPagination()
    const category = formValues.searchCategory
    const address = formValues.address.toLowerCase()
    const addressChanged = address !== searchAddress
    if (addressChanged) setSearchAddress(address)
    setSearchTerm(category)
    if (addressChanged) await fetchLocation()
    if (searchQuery.isStale) await fetchSearch()
    syncFormToQueryString({
      ...formValues,
      page: parseInt(queryStringValues.page ?? 0),
      serviceLanguage: queryStringValues.serviceLanguage || '',
    })
  }

  /**
   * @typedef {import('react-hook-form').FieldErrors<formSchema>} FormErrorType
   * @typedef {import('react').BaseSyntheticEvent} BaseSyntheticEvent
   */

  /**
   * Handler function for form errors
   * @param {FormErrorType} errors
   * @param {BaseSyntheticEvent} e
   */
  const onError = (errors, e) => {
    console.error('form errors', errors, e)
  }

  // Reset form field values.
  const onReset = () => {
    reset({
      searchCategory: '',
      address: '',
      searchDistance: '50',
    })

    setSearchDistance(50)
    setShowResults(false)

    syncFormToQueryString({
      searchCategory: '',
      address: '',
      searchDistance: '',
      serviceLanguage: '',
      page: '',
    })
  }

  useEffect(() => {
    if (searchData.RecordCount > 0) {
      const newLanguages = new Set([t('English'), t('French')])
      searchData.Records.forEach((record) => {
        const languages = record.LanguagesOfferedList.split(',')
          .map((item) =>
            item
              .split(' ')
              .map((s) => s && s.trim()[0].toLocaleUpperCase() + s.trim().slice(1))
              .join(' ')
              .trim()
          )
          .filter((item) => Boolean(item))
        languages.forEach((language) => {
          newLanguages.add(language)
        })
      })
      if (newLanguages.size > 2) setFiltersByLanguage(newLanguages)
    }
  }, [searchData])

  // Auto-execute search query when initial params pre-filled.
  useEffect(() => {
    const formValues = /** @type {formSchema} */ (getValues())
    if (formValues?.searchCategory && formValues?.address) {
      fetchLocation().then(() => {
        onSubmit({
          ...formValues,
          page: pageIndex,
        }).then(() => {
          if (queryStringValues?.page) {
            changePage(queryStringValues.page)
          }

          console.log('Submit was performed.')
        })
      })
    }
  }, [])

  const languageSelectValues = useMemo(() => {
    // sort all other languages besides English and French
    const languages = Array.from(filtersByLanguage)
      .filter((item) => item !== t('English') && item !== t('French'))
      .sort()
    // make sure that English and French are always the first two languages
    return [t('English'), t('French'), ...languages].map((language) => ({
      label: language,
      value: language,
    }))
  }, [filtersByLanguage])

  // console.log(searchParams)
  // console.log('### search results:', searchData)

  return (
    <div className="premade-search-form">
      <form
        ref={formRef}
        className="premade-search-form__form-content"
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        <div className="premade-search-form__form-fields">
          <div className="premade-search-form__form-field-item">
            <Controller
              control={control}
              name="searchCategory"
              defaultValue=""
              render={({ field }) => (
                <SelectInput
                  id="form-premade-search-category"
                  label={t('Choose Sub-Category')}
                  labelClasses={{
                    root: 'premade-search-form__form-field-label',
                  }}
                  placeholder={t('Choose from related sub-categories')}
                  error={Boolean(formErrors.searchCategory)}
                  // @ts-ignore
                  errorHelperText={formErrors.searchCategory?.message}
                  selectOptions={subcategoryOptions}
                  inputFields={field}
                />
              )}
            />
          </div>
          <div className="premade-search-form__form-field-item">
            <LocationField
              register={register}
              errors={formErrors}
              setValueCallback={setValue}
              resetValueCallback={resetField}
              errorCallback={setError}
              clearErrorCallback={clearErrors}
              getValues={getValues}
            />
          </div>
          <input type="hidden" {...register('searchDistance')} />
        </div>
        <div className="premade-search-form__form-actions">
          <Button
            type="submit"
            label={t('Find Services')}
            loadingLabel={t('Searching...')}
            isLoading={isDataLoading}
            buttonFields={{
              disabled: Object.keys(formErrors).length !== 0,
            }}
          />
          <Button
            type="reset"
            label={t('Clear Search')}
            variant="text"
            color={Object.keys(formErrors).length !== 0 ? 'error' : 'secondary'}
            buttonFields={{
              disabled: isDataLoading,
              onClick: onReset,
            }}
          />
        </div>
      </form>
      <div className="premade-search-form__search-results">
        {showResults && prevSearches.length > 0 && (
          <>
            <ResultsList
              isLoading={isDataLoading}
              data={searchData}
              geoData={geoData}
              startResult={startResult}
              endResult={endResult}
              sort={searchParams.sort}
              setSort={changeSort}
              removeDigital={searchParams.removeDigital}
              setRemoveDigital={setRemoveDigital}
              searchDistance={searchParams.distance}
              onDistanceChange={(/** @type {number} */ value) => changeDistance(value)}
              languageOptions={languageSelectValues}
              serviceLanguage={searchParams.serviceLanguage}
              onLanguageChange={(/** @type {string} */ value) => changeLanguage(value)}
              numPages={numPages}
              pageIndex={pageIndex}
              onPageChange={(/** @type {number} */ value) => changePage(value)}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default PremadeSearchWithResultsForm
